import { X, Check, Crown, TrendingUp, Download, Copy, BarChart3 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";

interface PaywallModalProps {
  isOpen: boolean;
  onClose: () => void;
  feature: string;
}

export default function PaywallModal({ isOpen, onClose, feature }: PaywallModalProps) {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleGoToPremium = () => {
    navigate('/login');
    onClose();
  };

  const featureDetails: Record<string, { icon: any; color: string }> = {
    save: { icon: BarChart3, color: '#3b82f6' },
    pdf: { icon: Download, color: '#10b981' },
    csv: { icon: Download, color: '#8b5cf6' },
    audit: { icon: TrendingUp, color: '#f59e0b' },
    scenarios: { icon: Copy, color: '#ef4444' }
  };

  const currentFeature = featureDetails[feature] || { icon: Crown, color: '#3b82f6' };
  const FeatureIcon = currentFeature.icon;

  return createPortal(
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        zIndex: 10000,
        padding: '0',
        backdropFilter: 'blur(4px)',
        overflowY: 'auto'
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '20px',
          maxWidth: '480px',
          width: '100%',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.25)',
          position: 'relative',
          overflow: 'hidden',
          margin: '60px 24px',
          maxHeight: 'calc(100vh - 120px)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            appearance: 'none',
            background: 'rgba(255, 255, 255, 0.2)',
            border: 'none',
            cursor: 'pointer',
            color: 'white',
            padding: '8px',
            borderRadius: '8px',
            transition: 'all 0.15s ease',
            zIndex: 1
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
          }}
        >
          <X size={24} />
        </button>

        <div style={{ maxHeight: 'calc(100vh - 120px)', overflowY: 'auto' }}>
          <div
            style={{
              background: `linear-gradient(135deg, ${currentFeature.color} 0%, ${currentFeature.color}dd 100%)`,
              padding: '32px 24px',
              textAlign: 'center'
            }}
          >
            <div
              style={{
                width: '64px',
                height: '64px',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 12px',
                border: '3px solid rgba(255, 255, 255, 0.3)'
              }}
            >
              <FeatureIcon size={32} color="white" />
            </div>

            <h2
              style={{
                margin: '0 0 8px',
                fontSize: '24px',
                fontWeight: 700,
                color: 'white',
              }}
            >
              Prueba Premium Gratis
            </h2>

            <p
              style={{
                margin: 0,
                fontSize: '14px',
                color: 'rgba(255, 255, 255, 0.95)',
                lineHeight: '1.5',
              }}
            >
              Regístrate y disfruta de todas las funciones sin compromiso
            </p>
          </div>

          <div style={{ padding: '24px' }}>
          <h3
            style={{
              margin: '0 0 16px',
              fontSize: '15px',
              fontWeight: 700,
              color: '#1e293b',
            }}
          >
            Ventajas principales:
          </h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
            {[
              'Detalle de IRPF (base, cuota total y anticipos)',
              'Deducciones por comunidad autónoma',
              'Proyección trimestral y análisis detallado',
              'Guardar escenarios y simulaciones',
              'Comparación de escenarios A/B',
              'Exportar informes PDF y CSV profesionales',
            ].map((item, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}
              >
                <div
                  style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    background: '#dcfce7',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}
                >
                  <Check size={14} color="#16a34a" strokeWidth={3} />
                </div>
                <span style={{ fontSize: '14px', color: '#475569', lineHeight: '1.5' }}>
                  {item}
                </span>
              </div>
            ))}
          </div>

          <button
            onClick={handleGoToPremium}
            style={{
              appearance: 'none',
              width: '100%',
              padding: '14px 24px',
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              fontSize: '16px',
              fontWeight: 700,
              cursor: 'pointer',
              transition: 'all 0.15s ease',
              boxShadow: '0 4px 16px rgba(59, 130, 246, 0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 8px 24px rgba(59, 130, 246, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 16px rgba(59, 130, 246, 0.3)';
            }}
          >
            <Crown size={18} />
            Regístrate gratis
          </button>

          <div style={{ textAlign: 'center', marginTop: '12px' }}>
            <p style={{ margin: 0, fontSize: '12px', color: '#94a3b8' }}>
              Sin permanencia · Cancela cuando quieras
            </p>
          </div>

          <button
            onClick={onClose}
            style={{
              appearance: 'none',
              width: '100%',
              padding: '10px 24px',
              backgroundColor: 'transparent',
              color: 'var(--muted)',
              border: 'none',
              borderRadius: '10px',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.15s ease',
              marginTop: '8px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = 'var(--text)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = 'var(--muted)';
            }}
          >
            Continuar con versión gratuita
          </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
